import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { safeStorage } from '@/lib/storage'
import { SessionExpired } from '@/auth/SessionExpired'
import { panelForPath } from '@/pages/settings/panels'
import { useActiveTenant } from '@/tenant/context'
import { SupportAccessBanner } from '@/shell/SupportAccessBanner'
import { AccountStatus } from '@/pages/AccountStatus'
import { SetupBanner } from './SetupBanner'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import type { Crumb } from './TopBar'
import {
  MODULE_STORAGE_KEY,
  moduleById,
  moduleForPath,
  rememberedModule,
  sectionForPath,
} from './navigation'
import type { ModuleId } from './navigation'

/**
 * The frame every signed-in screen sits inside.
 *
 * Converted once, from S-D3, so no later session redraws navigation — which is
 * the whole reason this session exists ahead of the module sessions.
 *
 * The module is derived from the URL where the URL says (`/seo`, `/outreach`)
 * and from the remembered choice where it does not (`/settings`, which belongs
 * to both). Storing it is what stops a settings visit from silently moving
 * somebody out of the module they were working in.
 */
export function AppShell(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const { expired } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // `safeStorage()` and not `window.localStorage`: the initialiser below runs
  // during render, and the storage getter itself throws where storage is
  // blocked for the origin, which would blank the shell (WEB-8).
  const storage = useMemo(() => safeStorage(), [])
  const fromPath = moduleForPath(location.pathname)
  const [remembered, setRemembered] = useState<ModuleId>(() => rememberedModule(storage))
  const module = fromPath ?? remembered

  useEffect(() => {
    if (fromPath) {
      setRemembered(fromPath)
      storage.setItem(MODULE_STORAGE_KEY, fromPath)
    }
  }, [fromPath, storage])

  // The drawer is a route-level overlay; leaving it open across a navigation
  // would cover the screen the user just asked for.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const onModuleChange = useCallback(
    (id: ModuleId) => {
      setRemembered(id)
      storage.setItem(MODULE_STORAGE_KEY, id)
      void navigate(moduleById(id).home)
    },
    [navigate, storage],
  )

  const crumbs = useMemo<Crumb[]>(() => {
    const definition = moduleById(module)
    const trail: Crumb[] = [
      { label: tenant.name, to: '/organisations' },
      { label: definition.label, to: definition.home },
    ]
    if (location.pathname.startsWith('/settings')) {
      trail.push({ label: 'Settings', to: '/settings/organisation' })
      const panel = panelForPath(location.pathname)
      if (panel) trail.push({ label: panel.label })
    } else {
      // S-B19. Read off the navigation model rather than hardcoded, because the
      // Inbox is the first section route and *Overview* was being shown on it.
      // A section that gets a route from here on gets its own breadcrumb with no
      // edit to this file. `Overview` is still the fallback: it is what the
      // module home is called, and it is what `/billing/*` — inside the shell,
      // not a module section — has always shown.
      const section = sectionForPath(location.pathname)
      trail.push({ label: section?.label ?? 'Overview' })
    }
    return trail
  }, [module, tenant.name, location.pathname])

  return (
    <div className="app-shell">
      {/* S-B11. Outside the routed area and above the sidebar, so no screen can
          omit it and it cannot be scrolled away from — which is what makes screen
          08's "the banner cannot be dismissed" a property of the layout rather
          than a promise. `null` on every ordinary session. */}
      <SupportAccessBanner />

      <Sidebar module={module} onModuleChange={onModuleChange} />

      <div className="app-main">
        <TopBar
          crumbs={crumbs}
          onOpenMenu={() => {
            setDrawerOpen(true)
          }}
        />

        <main className="app-body">
          <SetupBanner />
          {/* S-B32a. **The unconfirmed-address banner, moved here from
              `ModuleOverview`.** It used to render from the module landing
              placeholder, which meant it lived on whichever screens had not been
              built yet: S-B31a replaced the outreach one and S-B32a the SEO one,
              and between them the banner would have left the product entirely.

              The shell is where it always belonged. It is an *account* fact, not
              a module one — an unconfirmed address is why approval requests,
              digests and password resets silently never arrive, which presents
              to the user as "AugRank does not send me anything" — so it must not
              be something a future screen can drop by being written. It renders
              nothing when there is nothing to say. */}
          <AccountStatus />
          <Outlet />
        </main>

        <MobileBar module={module} />
      </div>

      {drawerOpen ? (
        <div
          className="ar-drawer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDrawerOpen(false)
          }}
        >
          <div className="ar-drawer__panel" style={{ inset: '0 auto 0 0' }}>
            <Sidebar
              module={module}
              variant="drawer"
              onModuleChange={onModuleChange}
              onNavigate={() => {
                setDrawerOpen(false)
              }}
            />
            <div className="ar-drawer__footer">
              <button
                type="button"
                className="ar-btn ar-btn--ghost ar-btn--block"
                onClick={() => {
                  setDrawerOpen(false)
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {expired ? <SessionExpired /> : null}
    </div>
  )
}

/**
 * The bottom bar below 1100px.
 *
 * The first four sections of the current module, so what a tenant reaches for
 * on a phone is one tap away. Sections whose module has not shipped keep their
 * place and say so, for the same reason the sidebar does.
 */
function MobileBar({ module }: { module: ModuleId }): JSX.Element {
  const definition = moduleById(module)
  return (
    <nav className="app-mobile-bar" aria-label={`${definition.label} (compact)`}>
      {definition.sections.slice(0, 4).map((section) =>
        section.to ? (
          <NavLink key={section.label} to={section.to} end={section.to === definition.home}>
            {section.label}
          </NavLink>
        ) : (
          <span key={section.label} aria-disabled="true">
            {section.label}
          </span>
        ),
      )}
    </nav>
  )
}
